/**
 * Acciones sobre un contrato: activar, renovar y rescindir.
 *
 * LAS TRES ENSEÑAN EL IMPACTO ANTES DE PEDIR CONFIRMACIÓN, y no por cortesía: las tres tocan
 * dinero. Activar emite cargos, renovar fija la renta del año siguiente y rescindir anula
 * cargos y libera la habitación. Un usuario que confirma sin ver las consecuencias no está
 * decidiendo, está apostando.
 *
 * El número que se muestra SIEMPRE viene del servidor (`/preview-*`), nunca se recalcula aquí.
 * Si la web hiciera su propia cuenta, tarde o temprano diferiría de la que se guarda — y la
 * diferencia aparecería justo en el importe de un alquiler.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { LeaseSummary, RenewalPreview, TerminationPreview } from '@naar/shared';
import { ApiError, api, queryKeys } from '../../api/client';
import { Button } from '../../components/Button';
import { Icon } from '../../components/Icon';
import { ConfirmDialog } from '../../components/overlay/Drawer';
import { Field, Input, MoneyInput, Select } from '../../components/form/Field';
import { toast } from '../../components/feedback/Toaster';
import { useAuth } from '../../auth/AuthContext';
import { formatEurCents, formatShortDate } from '../../lib/format';
import styles from './LeaseActions.module.css';

type Dialog = 'none' | 'activate' | 'renew' | 'terminate';

const TERMINATION_REASONS = [
  { value: 'fin_contrato', label: 'Fin de contrato' },
  { value: 'acuerdo_mutuo', label: 'Acuerdo mutuo' },
  { value: 'impago', label: 'Impago' },
  { value: 'incumplimiento', label: 'Incumplimiento de condiciones' },
  { value: 'venta_inmueble', label: 'Venta del inmueble' },
  { value: 'obra', label: 'Necesidad de obra' },
  { value: 'otro', label: 'Otro motivo' },
];

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

/** El día siguiente a una fecha ISO, en ISO. Ver el `min` de la fecha efectiva. */
function diaSiguiente(iso: string): string {
  const fecha = new Date(`${iso}T00:00:00Z`);
  fecha.setUTCDate(fecha.getUTCDate() + 1);
  return fecha.toISOString().slice(0, 10);
}

/**
 * Invalida todo lo que un cambio de contrato deja obsoleto.
 *
 * Se EXPORTA para que el asistente use exactamente la misma lista. Cuando no lo hacía, crear
 * un contrato invalidaba el LISTADO de inquilinos pero no la FICHA, y como el cliente tiene
 * `staleTime: 60_000`, el asistente te dejaba en la ficha del inquilino diciendo «Sin contrato
 * vigente» hasta un minuto después de haberlo creado. Dos listas separadas de claves es una
 * invitación a que una se quede corta.
 */
export function useLeaseInvalidation() {
  const queryClient = useQueryClient();
  return () => {
    // La ocupación, la renta contratada y el rent roll dependen del estado del contrato, así
    // que un cambio aquí desactualiza medio panel. Invalidar de menos es peor que de más.
    for (const key of [['tenant'], ['tenants'], ['properties'], ['property'], ['dashboard'], ['rent-charges'], ['lease'], ['units']]) {
      void queryClient.invalidateQueries({ queryKey: key });
    }
  };
}

export function LeaseActions({ lease }: { lease: LeaseSummary }) {
  const { can } = useAuth();
  const [dialog, setDialog] = useState<Dialog>('none');

  const isDraft = lease.status === 'borrador';
  const isActive = lease.status === 'activo';

  // Un contrato renovado o rescindido es histórico: no admite ninguna acción.
  if (!isDraft && !isActive) return null;

  return (
    <>
      <div className={styles.bar}>
        {isDraft && can('lease:activate') && (
          <Button variant="primary" size="sm" onClick={() => setDialog('activate')}>
            <Icon name="plus" size={15} strokeWidth={2.4} />
            Activar contrato
          </Button>
        )}

        {/* Con una renovación ya encadenada —la hoja subida que espera su día— no hay otra que crear. */}
        {isActive && can('lease:renew') && !lease.renewedByLeaseId && (
          <Button variant="secondary" size="sm" onClick={() => setDialog('renew')}>
            Renovar
          </Button>
        )}

        {isActive && can('lease:terminate') && (
          <Button variant="danger" size="sm" onClick={() => setDialog('terminate')}>
            Rescindir
          </Button>
        )}

        {isDraft && (
          <span className={styles.draftHint}>
            <Icon name="info" size={13} />
            En borrador: todavía no emite cargos ni ocupa la habitación.
          </span>
        )}
      </div>

      {dialog === 'activate' && (
        <ActivateDialog lease={lease} onClose={() => setDialog('none')} />
      )}
      {dialog === 'renew' && <RenewDialog lease={lease} onClose={() => setDialog('none')} />}
      {dialog === 'terminate' && (
        <TerminateDialog lease={lease} onClose={() => setDialog('none')} />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Activar
// ---------------------------------------------------------------------------

function ActivateDialog({ lease, onClose }: { lease: LeaseSummary; onClose: () => void }) {
  const invalidate = useLeaseInvalidation();

  // Los cargos que se van a emitir los calcula el SERVIDOR: son exactamente los que creará.
  const preview = useQuery({
    queryKey: queryKeys.chargePreview(lease.id),
    queryFn: () => api.chargePreview(lease.id),
  });

  const activate = useMutation({
    mutationFn: () => api.activateLease(lease.id),
    onSuccess: (result) => {
      invalidate();
      toast.success(
        `Contrato activado. Se han emitido ${result.chargesCreated} cargo${
          result.chargesCreated === 1 ? '' : 's'
        } de renta y la habitación ${lease.unit.code} queda ocupada.`,
      );
      onClose();
    },
    onError: (error: unknown) => {
      toast.error(error instanceof ApiError ? error.message : 'No se ha podido activar.');
    },
  });

  const charges = preview.data?.charges ?? [];

  return (
    <ConfirmDialog
      title="Activar el contrato"
      body={`La habitación ${lease.unit.code} pasará a ocupada y se emitirán los cargos de renta del periodo. Es reversible solo rescindiendo.`}
      confirmLabel="Activar y emitir cargos"
      onCancel={onClose}
      onConfirm={async () => { await activate.mutateAsync(); }}
    >
      {preview.isPending ? (
        <p className={styles.dialogNote}>Calculando los cargos…</p>
      ) : charges.length === 0 ? (
        <p className={styles.dialogNote}>
          No se emitirá ningún cargo con las fechas actuales del contrato.
        </p>
      ) : (
        <div className={styles.previewBox}>
          <div className={styles.previewHead}>
            <span>
              {charges.length} cargo{charges.length === 1 ? '' : 's'} a emitir
            </span>
            <strong>{formatEurCents(preview.data!.total)}</strong>
          </div>
          <ul className={styles.previewList}>
            {charges.slice(0, 6).map((charge) => (
              <li key={charge.periodStart}>
                <span>{charge.concept}</span>
                <span className={styles.previewDue}>vence {formatShortDate(charge.dueDate)}</span>
                <span className={styles.previewAmount}>{formatEurCents(charge.amount)}</span>
              </li>
            ))}
          </ul>
          {charges.length > 6 && (
            <p className={styles.previewMore}>y {charges.length - 6} más…</p>
          )}
        </div>
      )}
    </ConfirmDialog>
  );
}

// ---------------------------------------------------------------------------
// Renovar
// ---------------------------------------------------------------------------

function RenewDialog({ lease, onClose }: { lease: LeaseSummary; onClose: () => void }) {
  const invalidate = useLeaseInvalidation();
  const [rent, setRent] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [touched, setTouched] = useState(false);

  const preview = useQuery<RenewalPreview>({
    queryKey: queryKeys.renewalPreview(lease.id),
    queryFn: () => api.previewRenewal(lease.id),
  });

  // La propuesta del servidor rellena el formulario, pero el usuario manda: si toca la renta,
  // deja de sobrescribirse.
  if (preview.data && !touched && rent === '') {
    setRent(preview.data.suggestedRent);
    setStartDate(preview.data.suggestedStartDate);
    setEndDate(preview.data.suggestedEndDate ?? '');
    setTouched(true);
  }

  const renew = useMutation({
    mutationFn: () =>
      api.renewLease(lease.id, {
        startDate,
        endDate: endDate || null,
        monthlyRent: rent,
      }),
    onSuccess: () => {
      invalidate();
      toast.success(
        `Contrato renovado a ${formatEurCents(rent)}/mes. El anterior queda como «renovado» y el nuevo, en borrador.`,
      );
      onClose();
    },
    onError: (error: unknown) => {
      toast.error(error instanceof ApiError ? error.message : 'No se ha podido renovar.');
    },
  });

  return (
    <ConfirmDialog
      title="Renovar el contrato"
      body="Se crea un contrato NUEVO encadenado a este. El actual queda como «renovado» y conserva sus importes intactos, porque respalda los cargos ya emitidos."
      confirmLabel="Crear la renovación"
      onCancel={onClose}
      onConfirm={async () => { await renew.mutateAsync(); }}
    >
      {preview.isPending ? (
        <p className={styles.dialogNote}>Calculando la renta actualizada…</p>
      ) : (
        <div className={styles.renewBody}>
          {preview.data && preview.data.indexationType !== 'ninguna' && (
            /* El cálculo escrito tal cual lo hace el servidor. Ver un «× 1,021» explica la
               subida mucho mejor que un importe nuevo aparecido de la nada. */
            <p className={styles.calculation}>
              <Icon name="info" size={13} />
              {preview.data.calculation}
            </p>
          )}

          <Field
            label="Renta del nuevo contrato"
            required
            help="Puedes ajustarla: la propuesta es solo la indexación pactada."
          >
            <MoneyInput
              value={rent}
              onValueChange={setRent}
              onChange={() => undefined}
            />
          </Field>

          <div className={styles.dateRow}>
            <Field label="Inicio" required>
              <Input
                type="date"
                value={startDate}
                onChange={(event) => setStartDate(event.target.value)}
              />
            </Field>
            <Field label="Fin" optional help="Vacío = indefinido.">
              <Input
                type="date"
                value={endDate}
                onChange={(event) => setEndDate(event.target.value)}
              />
            </Field>
          </div>
        </div>
      )}
    </ConfirmDialog>
  );
}

// ---------------------------------------------------------------------------
// Rescindir
// ---------------------------------------------------------------------------

/**
 * Se EXPORTA porque la ficha del inquilino lo abre desde su propio «Marcar como inactivo».
 * Poner a alguien inactivo es exactamente rescindir su contrato —con sus cargos, su prorrateo
 * y su fianza—, así que reusar este diálogo es lo que impide que existan dos formas distintas
 * de terminar una estancia, una de ellas sin previsualización del impacto.
 */
export function TerminateDialog({ lease, onClose }: { lease: LeaseSummary; onClose: () => void }) {
  const invalidate = useLeaseInvalidation();
  const [reason, setReason] = useState('acuerdo_mutuo');
  const [effectiveAt, setEffectiveAt] = useState(today());

  const preview = useQuery<TerminationPreview>({
    queryKey: queryKeys.terminationPreview(lease.id, effectiveAt),
    queryFn: () => api.previewTermination(lease.id, effectiveAt),
    // Se recalcula al cambiar la fecha: el impacto depende por completo de ella.
    enabled: Boolean(effectiveAt),
  });

  const terminate = useMutation({
    mutationFn: (confirmation: string) =>
      api.terminateLease(lease.id, { reason, effectiveAt, confirmation }),
    onSuccess: () => {
      invalidate();
      toast.success(`Contrato rescindido. La habitación ${lease.unit.code} queda libre.`);
      onClose();
    },
    onError: (error: unknown) => {
      toast.error(error instanceof ApiError ? error.message : 'No se ha podido rescindir.');
    },
  });

  const impact = preview.data;

  return (
    <ConfirmDialog
      title="Rescindir el contrato"
      body="Se anulan los cargos futuros sin pagos, se prorratea el del periodo en curso y la habitación queda libre. No se puede deshacer."
      confirmLabel="Rescindir"
      tone="danger"
      /* Confirmación fuerte: hay que escribir el código de la habitación. El servidor lo comprueba
         también, así que no es solo una barrera visual. */
      requireText={lease.unit.code}
      requireTextHint={
        <>
          Para confirmar, escribe el código de la habitación: <code>{lease.unit.code}</code>
        </>
      }
      onCancel={onClose}
      onConfirm={async (typed) => { await terminate.mutateAsync(typed); }}
    >
      <div className={styles.renewBody}>
        <div className={styles.dateRow}>
          <Field label="Motivo" required>
            <Select
              options={TERMINATION_REASONS}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          </Field>
          <Field
            label="Fecha efectiva"
            required
            help="Determina qué se anula y qué se prorratea. Tiene que ser posterior al inicio."
          >
            <Input
              type="date"
              value={effectiveAt}
              /**
               * `min` = el día siguiente al inicio, porque la base exige `end_date > start_date`.
               * Sin esto el calendario deja elegir el propio día de inicio y el servidor lo
               * rechaza: mejor no ofrecer la fecha que explicar después por qué no vale.
               */
              min={diaSiguiente(lease.startDate)}
              onChange={(event) => setEffectiveAt(event.target.value)}
            />
          </Field>
        </div>

        {preview.isPending ? (
          <p className={styles.dialogNote}>Calculando el impacto…</p>
        ) : impact ? (
          <div className={styles.impactBox}>
            <p className={styles.impactTitle}>Qué va a pasar</p>
            <ul className={styles.impactList}>
              <li>
                <span className={styles.impactCount}>{impact.chargesToCancel.length}</span>
                <span>
                  cargo{impact.chargesToCancel.length === 1 ? '' : 's'} futuro
                  {impact.chargesToCancel.length === 1 ? '' : 's'} sin pagos se{' '}
                  <b>anulan</b>
                </span>
              </li>
              {impact.chargeToProrate && (
                <li>
                  <span className={styles.impactCount}>1</span>
                  <span>
                    cargo del periodo en curso se <b>prorratea</b> a{' '}
                    <b>{formatEurCents(impact.chargeToProrate.proratedAmount)}</b> (
                    {impact.chargeToProrate.daysOccupied} de{' '}
                    {impact.chargeToProrate.daysInPeriod} días)
                  </span>
                </li>
              )}
              <li>
                <span className={styles.impactCount}>{impact.untouchedCount}</span>
                <span>
                  cargo{impact.untouchedCount === 1 ? '' : 's'} ya cobrado
                  {impact.untouchedCount === 1 ? '' : 's'} <b>no se tocan</b>
                </span>
              </li>
              <li>
                <span className={styles.impactCount}>
                  {formatEurCents(impact.deposit.amount)}
                </span>
                <span>
                  de fianza en estado <b>{impact.deposit.status}</b>, que habrá que liquidar
                  aparte
                </span>
              </li>
            </ul>
            {Number(impact.balanceDue) > 0 && (
              <p className={styles.impactDue}>
                <Icon name="info" size={13} />
                Queda un saldo pendiente de {formatEurCents(impact.balanceDue)} que la rescisión
                no cancela.
              </p>
            )}
          </div>
        ) : null}
      </div>
    </ConfirmDialog>
  );
}
